import { useIntl } from 'react-intl';

import { ImportSource } from '../sources.ts';

import { notionDataFormatOptionsMessages } from './notion-messages.ts';
import { miroDataFormatOptionsMessages } from './miro-messages.ts';
import { muralDataFormatOptionsMessages } from './mural-messages.ts';

const NOTION_DATA_FORMAT_OPTIONS = [
	{ message: notionDataFormatOptionsMessages.mentions, value: 'mentions' },
	{ message: notionDataFormatOptionsMessages.audioEmbeddedLink, value: 'audio_embedded_link' },
	{ message: notionDataFormatOptionsMessages.attachments, value: 'attachments' },
	{ message: notionDataFormatOptionsMessages.blockEquation, value: 'block_equation' },
	{ message: notionDataFormatOptionsMessages.breadcrumb, value: 'breadcrumb' },
	{ message: notionDataFormatOptionsMessages.bulletedLists, value: 'bulleted_lists' },
	{ message: notionDataFormatOptionsMessages.button, value: 'button' },
	{ message: notionDataFormatOptionsMessages.callout, value: 'callout' },
	{ message: notionDataFormatOptionsMessages.codeBlock, value: 'code_block' },
	{ message: notionDataFormatOptionsMessages.codeMermaid, value: 'code_mermaid' },
	{ message: notionDataFormatOptionsMessages.twoColumns, value: 'two_columns' },
	{ message: notionDataFormatOptionsMessages.customizedText, value: 'customized_text' },
	{
		message: notionDataFormatOptionsMessages.databaseChildPageProperties,
		value: 'database_child_properties',
	},
	{ message: notionDataFormatOptionsMessages.dates, value: 'dates' },
	{ message: notionDataFormatOptionsMessages.dividers, value: 'dividers' },
	{ message: notionDataFormatOptionsMessages.embed, value: 'embed' },
	{ message: notionDataFormatOptionsMessages.emojis, value: 'emojis' },
	{ message: notionDataFormatOptionsMessages.headerImages, value: 'header_images' },
	{ message: notionDataFormatOptionsMessages.headings, value: 'headings' },
	{ message: notionDataFormatOptionsMessages.images, value: 'images' },
	{ message: notionDataFormatOptionsMessages.inlineEquation, value: 'inline_equation' },
	{ message: notionDataFormatOptionsMessages.linkToPage, value: 'link_to_page' },
	{ message: notionDataFormatOptionsMessages.numberedLists, value: 'numbered_lists' },
	{ message: notionDataFormatOptionsMessages.pageComments, value: 'page_comments' },
	{ message: notionDataFormatOptionsMessages.pageEmojis, value: 'page_emojis' },
	{ message: notionDataFormatOptionsMessages.quotes, value: 'quotes' },
	{ message: notionDataFormatOptionsMessages.reminder, value: 'reminder' },
	{ message: notionDataFormatOptionsMessages.staticTables, value: 'static_tables' },
	{ message: notionDataFormatOptionsMessages.syncedBlock, value: 'synced_block' },
	{ message: notionDataFormatOptionsMessages.tableCustomization, value: 'table_customization' },
	{ message: notionDataFormatOptionsMessages.tableData, value: 'table_data' },
	{ message: notionDataFormatOptionsMessages.tableOfContents, value: 'table_of_contents' },
	{ message: notionDataFormatOptionsMessages.todoList, value: 'todo_list' },
	{ message: notionDataFormatOptionsMessages.toggleHeading, value: 'toggle_heading' },
	{ message: notionDataFormatOptionsMessages.toggleList, value: 'toggle_list' },
	{ message: notionDataFormatOptionsMessages.videoEmbeddedLink, value: 'video_embedded_link' },
	{ message: notionDataFormatOptionsMessages.webBookmark, value: 'web_bookmark' },
	{ message: notionDataFormatOptionsMessages.other, value: 'other' },
];

const MIRO_DATA_FORMAT_OPTIONS = [
	{ message: miroDataFormatOptionsMessages.comments, value: 'comments' },
	{ message: miroDataFormatOptionsMessages.colors, value: 'colors' },
	{ message: miroDataFormatOptionsMessages.connectors, value: 'connectors' },
	{ message: miroDataFormatOptionsMessages.rotations, value: 'rotations' },
	{ message: miroDataFormatOptionsMessages.shapes, value: 'shapes' },
	{ message: miroDataFormatOptionsMessages.talkTracks, value: 'talk_tracks' },
	{ message: miroDataFormatOptionsMessages.lockedElements, value: 'locked_elements' },
	{ message: miroDataFormatOptionsMessages.stickyNotes, value: 'sticky_notes' },
	{ message: miroDataFormatOptionsMessages.frames, value: 'frames' },
	{ message: miroDataFormatOptionsMessages.borders, value: 'borders' },
	{ message: miroDataFormatOptionsMessages.draw, value: 'draw' },
	{ message: miroDataFormatOptionsMessages.websiteEmbeds, value: 'website_embeds' },
	{ message: miroDataFormatOptionsMessages.jiraCards, value: 'jira_cards' },
	{ message: miroDataFormatOptionsMessages.tables, value: 'tables' },
	{ message: miroDataFormatOptionsMessages.gifs, value: 'gifs' },
	{ message: miroDataFormatOptionsMessages.text, value: 'text' },
	{ message: miroDataFormatOptionsMessages.stamps, value: 'stamps' },
	{ message: miroDataFormatOptionsMessages.tags, value: 'tags' },
	{ message: miroDataFormatOptionsMessages.mindMaps, value: 'mind_maps' },
	{ message: miroDataFormatOptionsMessages.other, value: 'other' },
];

const MURAL_DATA_FORMAT_OPTIONS = [
	{ message: muralDataFormatOptionsMessages.borders, value: 'borders' },
	{ message: muralDataFormatOptionsMessages.colors, value: 'colors' },
	{ message: muralDataFormatOptionsMessages.comments, value: 'comments' },
	{ message: muralDataFormatOptionsMessages.connectors, value: 'connectors' },
	{ message: muralDataFormatOptionsMessages.draw, value: 'draw' },
	{ message: muralDataFormatOptionsMessages.gifs, value: 'gifs' },
	{ message: muralDataFormatOptionsMessages.lockedElements, value: 'locked_elements' },
	{ message: muralDataFormatOptionsMessages.mindMaps, value: 'mind_maps' },
	{ message: muralDataFormatOptionsMessages.rotations, value: 'rotations' },
	{ message: muralDataFormatOptionsMessages.shapes, value: 'shapes' },
	{ message: muralDataFormatOptionsMessages.stamps, value: 'stamps' },
	{ message: muralDataFormatOptionsMessages.stickyNotes, value: 'sticky_notes' },
	{ message: muralDataFormatOptionsMessages.tables, value: 'tables' },
	{ message: muralDataFormatOptionsMessages.tags, value: 'tags' },
	{ message: muralDataFormatOptionsMessages.text, value: 'text' },
	{ message: muralDataFormatOptionsMessages.websiteEmbeds, value: 'website_embeds' },
	{ message: muralDataFormatOptionsMessages.other, value: 'other' },
];

export const useDataFormatOptions = (): {
	value: string;
	label: string;
}[] => {
	const { formatMessage } = useIntl();

	return NOTION_DATA_FORMAT_OPTIONS.map((option) => ({
		value: option.value,
		label: formatMessage(option.message),
	}));
};

export const useDataFormatOptionsFromSource = (
	importSource?: ImportSource,
): {
	value: string;
	label: string;
}[] => {
	const { formatMessage } = useIntl();

	let dataFormatOptions;
	switch (importSource) {
		case ImportSource.NOTION:
			dataFormatOptions = NOTION_DATA_FORMAT_OPTIONS;
			break;
		case ImportSource.MIRO:
			dataFormatOptions = MIRO_DATA_FORMAT_OPTIONS;
			break;
		case ImportSource.MURAL:
			dataFormatOptions = MURAL_DATA_FORMAT_OPTIONS;
			break;
		default:
			// Should never happen
			dataFormatOptions = NOTION_DATA_FORMAT_OPTIONS;
	}

	return dataFormatOptions.map((option) => ({
		value: option.value,
		label: formatMessage(option.message),
	}));
};
